import { priceChangeReasonLabel } from '@barso/shared';
import { useDeferredValue, useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../components/icons';
import { SearchField } from '../components/SearchField';
import { Keypad, NoteButtons } from '../components/Keypad';
import { Alert, Button } from '../components/ui';
import { euro } from '../format';
import { NOT_RECEIVED_TEXT, changeFor, promoSavings, quickGiven } from '../pos/cart';
import { settlementText, type Correction } from '../pos/correction';
import { searchSales } from '../pos/historySearch';
import { addNote, pushDigits, removeLastDigit } from '../pos/keypad';
import { isReduced, type PromoView } from '../pos/promo';
import { shouldDismissSheet } from '../pos/sheetDrag';
import { standBarText, type StandHealth } from '../pos/standState';
import type { PosStore } from '../pos/store';
import type { CartLine, LocalSale } from '../pos/types';
import { SaleCard, type SaleSend } from './posSaleCard';
import type { PosHandle } from '../pos/usePos';
import { SaleNoteStatus } from './posNoteSheet';

/**
 * Teile der Kasse im Kassen-Modus (Plan „Kasse schneller bedienen“): Blätter (Sync-Stand, Bezahlen, Verkäufe), die
 * Zeile „Letzter Kunde“ und die Statusanzeige. Alles Geld Betreffende liegt weiter im Speicher (`PosStore`), diese
 * Teile zeigen nur an und melden Eingaben zurück.
 */

/**
 * Blatt über der Kasse: Titel, Schließen-Knopf, Escape schließt. Der Fokus landet beim Öffnen im Blatt. Ein Tipp auf den
 * Hintergrund und ein Wischen nach unten am Kopf schließen es ebenfalls, außer bei Fragen (`dismissible={false}`, ohne
 * Angabe gilt das für `alertdialog`): Eine Warnung oder Rückfrage soll bewusst beantwortet werden.
 */
export function Sheet({
  title,
  onClose,
  children,
  role = 'dialog',
  dismissible = role !== 'alertdialog',
  describedBy,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  role?: 'dialog' | 'alertdialog';
  dismissible?: boolean;
  /** Kennung eines Textes im Blatt, der den Hinweis ausführlich sagt (`aria-describedby`). */
  describedBy?: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; at: number } | null>(null);
  // Nur ein Tipp, der auf dem Hintergrund beginnt, schließt: Ein Ziehen aus dem Blatt heraus endet sonst als Klick dort
  const downOnBackdrop = useRef(false);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  useEffect(() => {
    const element = root.current;
    const backdrop = element?.parentElement;
    // Auslöser merken (vor dem Fokus ins Blatt); Hintergrund sperren, damit Tab und Lesegeräte nicht dahinter landen
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const locked: Element[] = [];
    for (const sibling of backdrop?.parentElement?.children ?? []) {
      if (sibling !== backdrop && !sibling.hasAttribute('inert')) {
        sibling.setAttribute('inert', '');
        locked.push(sibling);
      }
    }
    element?.focus();
    const onKey = (event: KeyboardEvent) => {
      // Liegen zwei Blätter übereinander (Storno-Rückfrage über „Letzter Verkauf“), schließt nur das oberste: das untere
      // ist dann gesperrt (`inert`)
      if (event.key === 'Escape' && backdrop?.hasAttribute('inert') !== true) close.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      for (const sibling of locked) sibling.removeAttribute('inert');
      if (opener?.isConnected === true) opener.focus();
    };
  }, []);
  const offset = (dy: number) => {
    if (root.current !== null)
      root.current.style.transform = dy > 0 ? `translateY(${String(dy)}px)` : '';
  };
  return (
    <div
      className="sheet-backdrop"
      onPointerDown={(down) => {
        downOnBackdrop.current = down.target === down.currentTarget;
      }}
      onClick={(click) => {
        const fromBackdrop = downOnBackdrop.current;
        downOnBackdrop.current = false;
        if (dismissible && fromBackdrop && click.target === click.currentTarget) onClose();
      }}
    >
      <div
        className="sheet"
        role={role}
        aria-modal="true"
        aria-label={title}
        aria-describedby={describedBy}
        tabIndex={-1}
        ref={root}
      >
        <div
          className="sheet__head"
          // Wischen nach unten am Kopf schließt (nicht bei Fragen); Knöpfe im Kopf bleiben tippbar
          style={dismissible ? { touchAction: 'none' } : undefined}
          onPointerDown={(down) => {
            if (
              !dismissible ||
              (down.target instanceof Element && down.target.closest('button') !== null)
            )
              return;
            drag.current = { x: down.clientX, y: down.clientY, at: down.timeStamp };
          }}
          onPointerMove={(move) => {
            if (drag.current === null) return;
            offset(move.clientY - drag.current.y);
          }}
          onPointerUp={(up) => {
            const start = drag.current;
            drag.current = null;
            offset(0);
            if (
              start !== null &&
              shouldDismissSheet({
                dx: up.clientX - start.x,
                dy: up.clientY - start.y,
                ms: up.timeStamp - start.at,
              })
            ) {
              onClose();
            }
          }}
          onPointerCancel={() => {
            drag.current = null;
            offset(0);
          }}
        >
          <h2>{title}</h2>
          <button
            type="button"
            className="sheet__close"
            aria-label="Schließen"
            title="Schließen"
            onClick={onClose}
          >
            <Icon name="close" size={24} />
          </button>
        </div>
        <div className="sheet__body stack">{children}</div>
      </div>
    </div>
  );
}

export type SyncView = {
  symbol: string;
  label: string;
  waiting: number;
  /** Farbe des Status-Punkts: grün, orange (schwaches Netz) oder rot (offline). */
  tone: 'ok' | 'warn' | 'bad';
};

/**
 * Stand der Verbindung für die Kopfzeile: Zustand, Symbol und Zahl der wartenden Vorgänge. Der Zustand kommt aus echten
 * Anfragen (`pos.connection`, U15a): Im WLAN ohne Internet meldet `navigator.onLine` weiter „online“.
 */
export function syncView(pos: PosHandle): SyncView {
  const label =
    pos.connection === 'offline'
      ? 'Offline'
      : pos.connection === 'weak' || pos.status?.state === 'error'
        ? 'Schwaches Netz'
        : 'Online';
  const symbol = label === 'Online' ? '🟢' : label === 'Offline' ? '🔴' : '🟠';
  const tone = label === 'Online' ? 'ok' : label === 'Offline' ? 'bad' : 'warn';
  return { symbol, label, waiting: pos.pending.sales + pos.pending.voids, tone };
}

function minutesAgo(date: Date | null, now: Date): string {
  if (date === null) return 'noch nie';
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000);
  return minutes < 1 ? 'gerade eben' : `vor ${String(minutes)} Min`;
}

/**
 * Inhalt des Blatts „Verbindung und Stand“ (U17c): Zustand, wartende Verkäufe, Artikelstand mit Uhrzeit und Zahl, „zuletzt
 * gesendet“, „Jetzt senden“ und der Weg zum Geräte-Check. Der Grund eines Warnzeichens (leerer oder alter Stand) steht hier.
 */
export function SyncDetails({
  pos,
  health = { kind: 'ok' },
  onDeviceCheck,
  onNote,
}: {
  pos: PosHandle;
  health?: StandHealth;
  onDeviceCheck?: () => void;
  /** „Etwas dazu sagen“ zu neuen Hinweisen (U17f). */
  onNote?: (() => void) | undefined;
}) {
  const view = syncView(pos);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 30_000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return (
    <>
      <div className="row row--between">
        <strong aria-live="polite">
          <span aria-hidden="true">{view.symbol}</span> {view.label}
        </strong>
        <Button
          variant="secondary"
          small
          onClick={pos.syncNow}
          disabled={pos.status?.state === 'syncing'}
        >
          Jetzt senden
        </Button>
      </div>
      <p className="muted">
        Zuletzt gesendet {minutesAgo(pos.status?.lastSuccessAt ?? null, now)}
        {pos.pending.failed > 0 &&
          ` · ${String(pos.pending.failed)} nicht übertragbar (siehe Verkäufe)`}
      </p>
      {pos.stand !== null && (
        <p>
          {pos.stand.snapshotAt === null
            ? 'Artikelstand: noch keiner geladen'
            : `Artikelstand ${TIME.format(pos.stand.snapshotAt)}`}
          {` · ${pos.stand.items.toLocaleString('de-DE')} Artikel`}
        </p>
      )}
      {health.kind === 'stale' && <Alert kind="info">{health.reason}</Alert>}
      {(health.kind === 'empty' || health.kind === 'loading') && (
        <Alert kind="info">{standBarText(health)}</Alert>
      )}
      {view.waiting > 0 && (
        <Alert kind="info">
          {view.waiting} {view.waiting === 1 ? 'Verkauf wartet' : 'Verkäufe warten'} auf
          Übertragung. Bitte lassen Sie die Kasse geöffnet, bis alles übertragen ist.
        </Alert>
      )}
      {(pos.unseenIssues.conflicts > 0 || pos.unseenIssues.rejected > 0) && (
        <Alert kind="info">
          <span className="stack">
            {pos.unseenIssues.conflicts > 0 && (
              <span>
                {pos.unseenIssues.conflicts === 1
                  ? '1 Artikel war schon an einer anderen Kasse verkauft.'
                  : `${String(pos.unseenIssues.conflicts)} Artikel waren schon an einer anderen Kasse verkauft.`}{' '}
                Die Leitung sieht das unter Kassen › Konflikte.
              </span>
            )}
            {pos.unseenIssues.rejected > 0 && (
              <span>
                {pos.unseenIssues.rejected === 1
                  ? '1 Verkauf nicht übertragbar'
                  : `${String(pos.unseenIssues.rejected)} Verkäufe nicht übertragbar`}{' '}
                (siehe Verkäufe).
              </span>
            )}
            {onNote !== undefined && (
              <span>
                Möchten Sie der Basar-Leitung etwas dazu sagen?{' '}
                <Button variant="secondary" small onClick={onNote}>
                  Etwas dazu sagen
                </Button>
              </span>
            )}
            <span>
              <Button variant="secondary" small onClick={pos.dismissIssues}>
                Verstanden
              </Button>
            </span>
          </span>
        </Alert>
      )}
      {pos.status?.lastSummary?.snapshotUnavailable === true && (
        <Alert kind="info">
          Artikelliste nicht mehr verfügbar (nur noch hochladen): Ihre Verkäufe werden übertragen,
          neue Artikel kommen nicht mehr dazu.
        </Alert>
      )}
      {pos.status?.lastError != null && view.label !== 'Online' && (
        <p className="muted">Letzter Fehler: {pos.status.lastError}</p>
      )}
      {onDeviceCheck !== undefined && (
        <Button variant="secondary" onClick={onDeviceCheck}>
          Geräte-Check
        </Button>
      )}
    </>
  );
}

const TIME = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Berlin',
});

const percentText = (basisPoints: number): string => `${String(basisPoints / 100)} %`;

/**
 * Banner der Rabattaktion (X7). Im Zeitraum bietet die Kasse die Aktion an, die Kassiererin startet sie nach Ansage VON HAND.
 * Läuft sie, steht „Aktion läuft“ mit „beenden“. Ein freier Rabatt ist nicht vorgesehen, es gibt nur diese Aktion.
 */
export function PromoBanner({
  offered,
  running,
  disabled,
  onStart,
  onStop,
}: {
  offered: PromoView | null;
  running: PromoView | null;
  disabled: boolean;
  onStart: (promo: PromoView) => void;
  onStop: () => void;
}) {
  if (running !== null) {
    return (
      <div className="cash__promo cash__promo--on" role="status">
        <span>
          <strong>{`Aktion −${percentText(running.basisPoints)} läuft`}</strong>
          {running.endsAtMs === null ? '' : ` · bis ${TIME.format(running.endsAtMs)}`}
        </span>
        <Button small variant="secondary" disabled={disabled} onClick={onStop}>
          Aktion beenden
        </Button>
      </div>
    );
  }
  if (offered === null) return null;
  return (
    <div className="cash__promo" role="status">
      <span>{`Aktion −${percentText(offered.basisPoints)} ab ${TIME.format(offered.startsAtMs)} – nach Ansage starten`}</span>
      <Button
        small
        disabled={disabled}
        onClick={() => {
          onStart(offered);
        }}
      >
        Aktion starten
      </Button>
    </div>
  );
}

/**
 * Preis einer Warenkorbzeile: bei Aktion der alte Preis durchgestrichen, der Aktionspreis und der Rabatt; bei einer Korrektur
 * an der Kasse (U17g) der alte Preis durchgestrichen und „korrigiert“.
 */
export function LinePrice({ line }: { line: CartLine }) {
  if (line.priceChange !== undefined) {
    return (
      <strong>
        <s className="muted cash__was">{euro(line.priceChange.originalCents)}</s>{' '}
        {euro(line.priceCents)} <span className="badge">korrigiert</span>
      </strong>
    );
  }
  if (!isReduced(line) || line.promo === null || line.promo === undefined) {
    return <strong>{euro(line.priceCents)}</strong>;
  }
  return (
    <strong>
      <s className="muted cash__was">{euro(line.promo.listPriceCents)}</s> {euro(line.priceCents)}{' '}
      <span className="badge">{`−${percentText(line.promo.basisPoints)}`}</span>
    </strong>
  );
}

/**
 * Zeile „inkl. Aktion −20 % · 3,10 € gespart“ (U18i); `null` ohne Ersparnis. Der Prozentsatz steht nur, wenn alle
 * Aktionszeilen denselben haben. Preiskorrekturen zählen nicht (siehe `promoSavings`).
 */
export function promoSavingsText(lines: readonly CartLine[]): string | null {
  const savings = promoSavings(lines);
  if (savings <= 0) return null;
  const rates = new Set(
    lines.flatMap((line) =>
      line.priceChange === undefined && isReduced(line) && line.promo != null
        ? [line.promo.basisPoints]
        : [],
    ),
  );
  const [only] = [...rates];
  const rate = rates.size === 1 && only !== undefined ? ` −${percentText(only)}` : '';
  return `inkl. Aktion${rate} · ${euro(savings)} gespart`;
}

/** Nach der Wahl des Betrags ignoriert die Bestätigung Tipps für diese Zeit: ein Doppeltipp trifft sonst „Bezahlt“. */
export const PAY_CONFIRM_LOCK_MS = 500;
/** Folgt ein Enter so kurz auf Zeichen, kommt es von einem Scanner und nicht von der Hand. */
const SCANNER_GAP_MS = 100;

/**
 * Bezahlen in zwei Schritten: Erst den gegebenen Betrag wählen (ein Tipp auf „Passend“ oder einen Schein, „Anderer Betrag“
 * mit Ziffernblock, „Ohne Betrag“), dann bestätigen: Summe, Gegeben, Rückgeld und Ersparnis stehen groß da, erst
 * „Bezahlt – nächster Kunde“ schließt den Verkauf ab. Gespeichert wird einmal: Der Knopf sperrt sich beim Speichern, die
 * Kasse hält zusätzlich eine Sperre (`payLock`), und die ersten 500 ms nach der Wahl nehmen keine Tipps an (Doppeltipp auf
 * einen Schein). Ein Scan im offenen Blatt wird ignoriert; ein Enter kurz nach Zeichen (Scanner) ebenfalls.
 */
export function PaySheet({
  total,
  lines = [],
  correction = null,
  cardOffered = false,
  busy,
  error,
  onPay,
  onClose,
  backRef,
}: {
  total: number;
  /** Zeilen des Warenkorbs, nur für die Zeile mit der Ersparnis durch eine Aktion (U18i). */
  lines?: readonly CartLine[];
  /** Korrektur eines Verkaufs (K5): nur ein Hinweis zur Verrechnung, am Kassieren ändert sich nichts. */
  correction?: Correction | null;
  /**
   * Der Basar nutzt die Zahlart „Karte“ (U14) und der Stand meldet sie: Dann gibt es den Knopf „Karte“. Nur eine Markierung
   * für das eigene Gerät des Vereins; BARSO bucht nichts ab und kennt kein Rückgeld.
   */
  cardOffered?: boolean;
  busy: boolean;
  error: string | null;
  /** Bar: nur der Betrag (`null` = ohne Betrag). Karte: `(null, 'card')`. */
  onPay: (givenCents: number | null, payment?: 'card') => void;
  onClose: () => void;
  /** Die Zurück-Taste der Hülle: im Bestätigungsschritt geht sie einen Schritt zurück (liefert dann `true`). */
  backRef?: { current: (() => boolean) | null } | undefined;
}) {
  const [other, setOther] = useState(false);
  // Ganze Cent, nie Text oder Gleitkomma (K6); 0 = noch nichts eingegeben
  const [givenCents, setGivenCents] = useState(0);
  // Bestätigungsschritt: der gewählte Betrag (`null` = ohne Betrag)
  const [chosen, setChosen] = useState<{ given: number | null; card?: boolean } | null>(null);
  const [scanHint, setScanHint] = useState(false);
  const shownAt = useRef(0);
  const confirmBox = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const wasChosen = useRef(false);
  const lastChar = useRef(0);
  const change = givenCents === 0 ? null : changeFor(total, givenCents);
  const quick = quickGiven(total)
    .filter((option) => option !== total)
    .slice(0, 4);
  const savingsText = promoSavingsText(lines);
  const confirming = chosen !== null;
  const given = chosen?.given ?? null;
  const back = given === null ? null : changeFor(total, given);

  const stepBack = () => {
    setChosen(null);
    setScanHint(false);
  };
  const settled = () => performance.now() - shownAt.current >= PAY_CONFIRM_LOCK_MS;
  const choose = (value: number | null) => {
    shownAt.current = performance.now();
    setChosen({ given: value });
  };
  const chooseCard = () => {
    shownAt.current = performance.now();
    setChosen({ given: null, card: true });
  };
  const card = chosen?.card === true;

  useEffect(() => {
    if (backRef === undefined) return;
    backRef.current = () => {
      if (!confirming) return false;
      stepBack();
      return true;
    };
    return () => {
      backRef.current = null;
    };
  }, [backRef, confirming]);

  // Fokus: in die Bestätigung auf den Block (nicht auf den Knopf: ein Tipp-Nachläufer träfe ihn), zurück auf „Passend“
  useEffect(() => {
    if (chosen !== null) {
      wasChosen.current = true;
      confirmBox.current?.focus();
    } else if (wasChosen.current) {
      wasChosen.current = false;
      grid.current?.querySelector('button')?.focus();
    }
  }, [chosen]);

  // Scanner-Folge im Bestätigungsschritt: Enter kurz nach Zeichen verwerfen, kurzer Hinweis
  useEffect(() => {
    if (!confirming) return;
    const onKey = (event: KeyboardEvent) => {
      const now = performance.now();
      if (event.key.length === 1) {
        lastChar.current = now;
      } else if (event.key === 'Enter' && now - lastChar.current < SCANNER_GAP_MS) {
        event.preventDefault();
        event.stopPropagation();
        setScanHint(true);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
    };
  }, [confirming]);

  const summary =
    chosen === null
      ? ''
      : card
        ? `Summe ${euro(total)}, Zahlart Karte, kein Rückgeld. Zum Abschließen „Bezahlt – nächster Kunde“ tippen.`
        : given === null
          ? `Summe ${euro(total)}, ohne Betrag, kein Rückgeld. Zum Abschließen „Bezahlt – nächster Kunde“ tippen.`
          : `Summe ${euro(total)}, gegeben ${euro(given)}, Rückgeld ${euro(back ?? 0)}. Zum Abschließen „Bezahlt – nächster Kunde“ tippen.`;
  const correctionNote = correction !== null && (
    <div className="cash__settle" role="note">
      <strong>{settlementText(correction, total, euro)}</strong>
      <span className="muted">
        {correction.payment === 'card'
          ? 'Karte ist nur eine Markierung: Die Zahlung läuft am eigenen Kartengerät, BARSO bucht nichts ab.'
          : confirming
            ? 'Gegeben und Rückgeld oben gelten für die volle neue Summe.'
            : 'Nur eine Hilfe: Die Differenz bar verrechnen und dann „Passend“ tippen. „Gegeben“ und Rückgeld unten gelten für die volle neue Summe.'}
      </span>
    </div>
  );
  return (
    // Beim Kassieren hält man Geld in der Hand: ein Tipp daneben oder ein Wisch soll das Blatt nicht schließen (nur
    // „Schließen“, Escape, Zurück-Taste). Im Bestätigungsschritt gehen Escape und Schließen einen Schritt zurück.
    <Sheet title="Bezahlen" dismissible={false} onClose={confirming ? stepBack : onClose}>
      <p className="sr-only" role="status">
        {summary}
      </p>
      {savingsText !== null && <p className="pay-confirm__promo">{savingsText}</p>}
      <p className="cash__sum cash__sum--sheet">{`Summe: ${euro(total)}`}</p>
      {correctionNote}
      {error !== null && <Alert kind="error">{error}</Alert>}
      {chosen !== null ? (
        <div
          className="stack pay-confirm"
          role="group"
          aria-label="Bestätigen"
          tabIndex={-1}
          ref={confirmBox}
        >
          {card ? (
            <>
              <p>
                Zahlart: <strong>Karte</strong>
              </p>
              <p className="muted">
                Den Betrag nehmen Sie an Ihrem eigenen Kartengerät entgegen. BARSO bucht nichts ab
                und merkt sich nur die Zahlart, ohne Rückgeld.
              </p>
            </>
          ) : given === null ? (
            <p className="muted">Ohne Betrag: kein Rückgeld.</p>
          ) : (
            <>
              <p>
                Gegeben: <strong>{euro(given)}</strong>
              </p>
              <p className="pay-confirm__change" aria-live="polite">
                Rückgeld: <strong>{euro(back ?? 0)}</strong>
              </p>
            </>
          )}
          {given !== null && back === null && (
            <Alert kind="error">Das Gegebene reicht nicht für die Summe.</Alert>
          )}
          {scanHint && (
            <p className="muted" role="status">
              Erst „Bezahlt“ tippen, dann den nächsten Kunden scannen.
            </p>
          )}
          <div className="pay-confirm__actions">
            <Button
              className="pay-confirm__done"
              busy={busy}
              disabled={busy || (given !== null && back === null)}
              onClick={() => {
                if (!settled()) return;
                if (card) onPay(null, 'card');
                else onPay(given);
              }}
            >
              Bezahlt – nächster Kunde
            </Button>
            <Button
              icon="arrow-left"
              variant="secondary"
              disabled={busy}
              onClick={() => {
                if (!settled()) return;
                stepBack();
              }}
            >
              Zurück
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="pay-grid" role="group" aria-label="Gegeben" ref={grid}>
            <Button
              className="pay-grid__wide"
              disabled={busy}
              onClick={() => {
                choose(total);
              }}
            >
              Passend
            </Button>
            {/* Mit offenem Ziffernblock nur „Passend“: Sonst läge „Weiter“ am Handy unter dem Rand des Blatts */}
            {(other ? [] : quick).map((option) => (
              <Button
                key={option}
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  choose(option);
                }}
              >
                {euro(option)}
              </Button>
            ))}
            {/* Karte (U14): direkt nach „Passend“ und den Schnellbeträgen, mit offenem Ziffernblock ausgeblendet */}
            {cardOffered && !other && (
              <Button
                className="pay-grid__wide"
                variant={correction?.payment === 'card' ? 'primary' : 'secondary'}
                disabled={busy}
                onClick={chooseCard}
              >
                Karte (eigenes Gerät)
              </Button>
            )}
          </div>
          {!other && (
            <Button
              variant="secondary"
              onClick={() => {
                setOther(true);
              }}
            >
              Anderer Betrag
            </Button>
          )}
          {other && (
            <div className="stack">
              <p className="cash__given" aria-live="polite">
                Gegeben: <strong>{euro(givenCents)}</strong>
              </p>
              {givenCents !== 0 && change === null && (
                <p className="field__error" role="alert">
                  Das reicht nicht.
                </p>
              )}
              {change !== null && (
                <p aria-live="polite">
                  Rückgeld: <strong>{euro(change)}</strong>
                </p>
              )}
              <NoteButtons
                disabled={busy}
                onAdd={(cents) => {
                  setGivenCents((current) => addNote(current, cents));
                }}
              />
              <Keypad
                disabled={busy}
                onDigits={(digits) => {
                  setGivenCents((current) => pushDigits(current, digits));
                }}
                onBackspace={() => {
                  setGivenCents(removeLastDigit);
                }}
              />
              <div className="row">
                <Button
                  variant="secondary"
                  disabled={busy || givenCents === 0}
                  onClick={() => {
                    setGivenCents(0);
                  }}
                >
                  Zurücksetzen
                </Button>
                <Button
                  icon="arrow-right"
                  disabled={givenCents === 0 || change === null}
                  onClick={() => {
                    choose(givenCents);
                  }}
                >
                  Weiter
                </Button>
              </div>
            </div>
          )}
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => {
              choose(null);
            }}
          >
            Ohne Betrag kassieren
          </Button>
        </>
      )}
    </Sheet>
  );
}

/** Alle lokalen Verkäufe dieser Kasse als Karten (U17e) mit Suche (O6) und Storno. Die Suche läuft ganz auf dem Gerät. */
export function SalesSheet({
  store,
  version,
  send,
  cartEmpty,
  onVoid,
  onCorrect,
  onNote,
  onClose,
}: {
  store: PosStore;
  version: number;
  send: SaleSend;
  cartEmpty: boolean;
  /** „Problem melden“ zu einem Verkauf (U17f). */
  onNote?: ((sale: LocalSale) => void) | undefined;
  onVoid: (sale: LocalSale, lineNo?: number) => void;
  onCorrect: (sale: LocalSale) => void;
  onClose: () => void;
}) {
  const [sales, setSales] = useState<LocalSale[] | null>(null);
  const [q, setQ] = useState('');
  // Aufgeklappt: was die Person gewählt hat; ohne Wahl nur der letzte Verkauf
  const [chosen, setChosen] = useState<Record<string, boolean>>({});
  const deferred = useDeferredValue(q);
  useEffect(() => {
    const guard = { cancelled: false };
    void store.history(5000).then((all) => {
      if (!guard.cancelled) setSales(all);
    });
    return () => {
      guard.cancelled = true;
    };
  }, [store, version]);
  const hits = sales === null ? [] : searchSales(sales, deferred);
  const lastUuid = sales?.[0]?.clientUuid;
  // Mit Suchbegriff stehen die Treffer aufgeklappt da (man sucht ja die Positionen), sonst nur der letzte Verkauf
  const searching = deferred.trim() !== '';
  const openByDefault = (uuid: string): boolean => searching || uuid === lastUuid;
  return (
    <Sheet title="Verkäufe dieser Kasse" onClose={onClose}>
      <SearchField
        label="Verkäufe suchen"
        hint="Code, Verkaufsnummer, Verkäufernummer oder Bezeichnung"
        value={q}
        onChange={setQ}
        maxLength={60}
        inputMode="text"
        count={sales === null ? undefined : { shown: hits.length, total: sales.length }}
      />
      {sales === null && <p role="status">Einen Moment bitte …</p>}
      {sales?.length === 0 && <p className="muted">Noch keine Verkäufe.</p>}
      {sales !== null && sales.length > 0 && hits.length === 0 && (
        <p className="muted">Kein Verkauf gefunden.</p>
      )}
      {hits.length > 0 && (
        <ul className="list sale-cards">
          {hits.map((sale) => (
            <SaleCard
              key={sale.clientUuid}
              sale={sale}
              send={send}
              expanded={chosen[sale.clientUuid] ?? openByDefault(sale.clientUuid)}
              onToggle={() => {
                setChosen((before) => ({
                  ...before,
                  [sale.clientUuid]: !(before[sale.clientUuid] ?? openByDefault(sale.clientUuid)),
                }));
              }}
              cartEmpty={cartEmpty}
              onVoid={onVoid}
              onCorrect={onCorrect}
              onNote={onNote}
              extra={
                <SaleNoteStatus store={store} saleClientUuid={sale.clientUuid} version={version} />
              }
            />
          ))}
        </ul>
      )}
    </Sheet>
  );
}

/** Bestätigung der Eingabe „17/5“ (O3): Bezeichnung, Größe und Preis müssen zum Artikel in der Hand passen. */
export function RefConfirmSheet({
  sellerNumber,
  itemNo,
  line,
  onAnswer,
}: {
  sellerNumber: number;
  itemNo: number;
  line: CartLine;
  onAnswer: (yes: boolean) => void;
}) {
  return (
    <Sheet
      title={`Artikel ${String(sellerNumber)}/${String(itemNo)}`}
      dismissible={false}
      onClose={() => {
        onAnswer(false);
      }}
    >
      <strong>{line.title}</strong>
      <span>{line.size === null ? 'ohne Größe' : `Größe ${line.size}`}</span>
      <p className="cash__sum cash__sum--sheet">{euro(line.priceCents)}</p>
      <div className="row">
        <Button
          onClick={() => {
            onAnswer(true);
          }}
        >
          Ja, kassieren
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            onAnswer(false);
          }}
        >
          Abbrechen
        </Button>
      </div>
    </Sheet>
  );
}

/** Der Artikel liegt schon in einem geparkten Korb (X4). */
export function ParkHintSheet({
  parkNo,
  line,
  onAnswer,
}: {
  parkNo: number;
  line: CartLine;
  onAnswer: (choice: 'anyway' | 'open' | 'cancel') => void;
}) {
  return (
    <Sheet
      title="Geparkter Korb"
      role="alertdialog"
      onClose={() => {
        onAnswer('cancel');
      }}
    >
      <strong>{`„${line.title}“ liegt in geparktem Korb P${String(parkNo)}.`}</strong>
      <div className="row">
        <Button
          onClick={() => {
            onAnswer('open');
          }}
        >
          {`Korb P${String(parkNo)} öffnen`}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            onAnswer('anyway');
          }}
        >
          Trotzdem aufnehmen
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            onAnswer('cancel');
          }}
        >
          Abbrechen
        </Button>
      </div>
    </Sheet>
  );
}

/**
 * Rückfrage beim Verlassen der Kasse mit vollem Warenkorb (Zurück-Taste): Parken rettet den Korb, Verwerfen wirft ihn weg,
 * Bleiben macht nichts. Kein Geld betroffen, aber die Arbeit des Kunden geht sonst verloren.
 */
export function LeaveSheet({
  count,
  totalCents,
  canPark,
  onAnswer,
}: {
  count: number;
  totalCents: number;
  canPark: boolean;
  onAnswer: (choice: 'park' | 'discard' | 'stay') => void;
}) {
  return (
    <Sheet
      title="Kasse verlassen?"
      role="alertdialog"
      onClose={() => {
        onAnswer('stay');
      }}
    >
      <strong>{`Der Warenkorb (${String(count)} Artikel, ${euro(totalCents)}) geht verloren.`}</strong>
      <div className="row">
        {canPark && (
          <Button
            onClick={() => {
              onAnswer('park');
            }}
          >
            Parken und verlassen
          </Button>
        )}
        <Button
          variant="danger"
          onClick={() => {
            onAnswer('discard');
          }}
        >
          Verwerfen
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            onAnswer('stay');
          }}
        >
          Bleiben
        </Button>
      </div>
    </Sheet>
  );
}

/** Wie lange die Leiste „Rückgängig“ steht (K1). */
export const UNDO_MS = 8000;

/**
 * Leiste „… entfernt · Rückgängig“ über der Fußleiste. Sie nimmt keinen Fokus. Der Zeitgeber pausiert, solange Maus oder
 * Fokus auf ihr liegen (WCAG 2.2.1), und läuft danach mit der Restzeit weiter. Die Ansage übernimmt der umgebende
 * Bereich mit `role="status"` (Kasse).
 */
export function UndoBar({
  text,
  onUndo,
  onExpire,
}: {
  text: string;
  onUndo: () => void;
  onExpire: () => void;
}) {
  // Maus und Fokus getrennt: Verlässt die Maus die Leiste, während der Knopf noch den Fokus hat, bleibt sie stehen
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const paused = hovered || focused;
  const left = useRef(UNDO_MS);
  const expire = useRef(onExpire);
  useEffect(() => {
    expire.current = onExpire;
  });
  useEffect(() => {
    if (paused) return;
    const startedAt = Date.now();
    const timer = setTimeout(() => {
      expire.current();
    }, left.current);
    return () => {
      clearTimeout(timer);
      left.current = Math.max(0, left.current - (Date.now() - startedAt));
    };
  }, [paused]);
  return (
    <div
      className="cash__undo"
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      onFocus={() => {
        setFocused(true);
      }}
      onBlur={() => {
        setFocused(false);
      }}
    >
      <span>{text}</span>
      <Button small variant="secondary" onClick={onUndo}>
        Rückgängig
      </Button>
    </div>
  );
}

/** Blatt „Artikel“ (Antippen einer Warenkorbzeile): Einzelheiten und der große Knopf zum Entfernen (ohne Wischen nutzbar). */
export function ItemSheet({
  line,
  note,
  onChangePrice,
  onClearPrice,
  onRemove,
  onClose,
}: {
  line: CartLine;
  /** Zusatz wie „keine Aktion“. */
  note?: string;
  /** „Preis korrigieren“ (U17g): öffnet das Blatt mit Ziffernblock und Grund. */
  onChangePrice?: () => void;
  /** Nimmt eine Korrektur zurück (U17g). */
  onClearPrice?: () => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet title="Artikel" onClose={onClose}>
      <strong>{line.title}</strong>
      <span>
        {line.sellerNumber === null
          ? 'unbekannter Artikel'
          : `Verkäufer ${String(line.sellerNumber)}`}
        {line.itemNo === null || line.itemNo === undefined
          ? ''
          : ` · Artikel ${String(line.itemNo)}`}
        {line.size === null ? '' : ` · Größe ${line.size}`}
      </span>
      {line.override === 'not_received' && <span className="muted">{NOT_RECEIVED_TEXT}</span>}
      {line.override !== null && line.override !== 'not_received' && (
        <span className="muted">Trotz Warnung aufgenommen.</span>
      )}
      {note !== undefined && <span className="muted">{note}</span>}
      <p className="cash__sum cash__sum--sheet">
        <LinePrice line={line} />
      </p>
      {line.priceChange !== undefined && (
        <span className="muted">{`Preis korrigiert: ${priceChangeReasonLabel(line.priceChange.reason)}`}</span>
      )}
      {onChangePrice !== undefined && (
        <Button variant="secondary" onClick={onChangePrice}>
          Preis korrigieren
        </Button>
      )}
      {onClearPrice !== undefined && line.priceChange !== undefined && (
        <Button variant="secondary" onClick={onClearPrice}>
          Korrektur zurücknehmen
        </Button>
      )}
      <Button variant="danger" onClick={onRemove}>
        Aus dem Warenkorb entfernen
      </Button>
    </Sheet>
  );
}

/**
 * Blatt „Letzter Verkauf“ (Symbol ↶ in der Kopfzeile): dieselbe Karte wie in der Liste, immer aufgeklappt, mit Sendestatus,
 * Hinweisen in Klartext, Korrigieren und Stornieren. Zeigt nur an; das Stornieren fragt `VoidConfirmSheet` ab und läuft über
 * den bisherigen Weg der Kasse.
 */
export function LastSaleSheet({
  sale,
  send,
  cartEmpty,
  onVoid,
  onCorrect,
  onAll,
  onNote,
  notes,
  onClose,
}: {
  sale: LocalSale;
  send: SaleSend;
  /** „Problem melden“ zu diesem Verkauf (U17f). */
  onNote?: ((sale: LocalSale) => void) | undefined;
  /** Kommentare der Kasse zu diesem Verkauf, schon als Text (U17f). */
  notes?: ReactNode;
  /** „Korrigieren“ geht nur bei leerem Warenkorb. */
  cartEmpty: boolean;
  onVoid: (sale: LocalSale, lineNo?: number) => void;
  onCorrect: (sale: LocalSale) => void;
  onAll: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet title="Letzter Verkauf" onClose={onClose}>
      <ul className="list sale-cards">
        <SaleCard
          sale={sale}
          send={send}
          expanded
          cartEmpty={cartEmpty}
          onVoid={onVoid}
          onCorrect={onCorrect}
          onNote={onNote}
          extra={notes}
        />
      </ul>
      <Button variant="secondary" onClick={onAll}>
        Alle Verkäufe
      </Button>
    </Sheet>
  );
}
